import React, { useState, useEffect } from 'react';
import { X, Download, Smartphone, Monitor, Check } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [platform, setPlatform] = useState<'ios' | 'android' | 'desktop' | 'other'>('other');

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Detect platform
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    const isAndroid = /android/.test(userAgent);
    const isDesktop = !isIOS && !isAndroid;

    if (isIOS) {
      setPlatform('ios');
    } else if (isAndroid) {
      setPlatform('android');
    } else if (isDesktop) {
      setPlatform('desktop');
    }

    // Listen for beforeinstallprompt event (Chrome, Edge, Android)
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      const installEvent = e as BeforeInstallPromptEvent;
      setDeferredPrompt(installEvent);
      
      // Show prompt after 10 seconds if user hasn't dismissed it before
      const hasBeenDismissed = localStorage.getItem('sentlo_install_dismissed');
      const dismissedTime = hasBeenDismissed ? parseInt(hasBeenDismissed) : 0;
      const daysSinceDismissal = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
      
      // Show again after 3 days
      if (!hasBeenDismissed || daysSinceDismissal > 3) {
        setTimeout(() => {
          setShowPrompt(true);
        }, 10000); // Show after 10 seconds
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Listen for successful installation
    window.addEventListener('appinstalled', () => {
      console.log('✅ Sentlo PWA installed successfully!');
      setIsInstalled(true);
      setShowPrompt(false);
      localStorage.removeItem('sentlo_install_dismissed');
    });

    // For iOS, show manual instructions after delay
    if (isIOS) {
      const hasBeenDismissed = localStorage.getItem('sentlo_install_dismissed');
      const dismissedTime = hasBeenDismissed ? parseInt(hasBeenDismissed) : 0;
      const daysSinceDismissal = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
      
      if (!hasBeenDismissed || daysSinceDismissal > 3) {
        setTimeout(() => {
          setShowPrompt(true);
        }, 15000); // Show after 15 seconds for iOS
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      // For iOS or if prompt not available, just show instructions
      return;
    }

    setIsInstalling(true);

    try {
      // Show the install prompt
      await deferredPrompt.prompt();
      
      // Wait for user choice
      const choiceResult = await deferredPrompt.userChoice;
      
      if (choiceResult.outcome === 'accepted') {
        console.log('✅ User accepted the install prompt');
        setShowPrompt(false);
        localStorage.removeItem('sentlo_install_dismissed');
      } else {
        console.log('❌ User dismissed the install prompt');
        handleDismiss();
      }
      
      setDeferredPrompt(null);
    } catch (error) {
      console.error('Error during installation:', error);
    } finally {
      setIsInstalling(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('sentlo_install_dismissed', Date.now().toString());
  };

  // Don't show if already installed
  if (isInstalled) {
    return null;
  }

  // Don't show if user dismissed
  if (!showPrompt) {
    return null;
  }

  // iOS Instructions
  if (platform === 'ios') {
    return (
      <div className="fixed bottom-20 left-4 right-4 z-50 md:left-auto md:right-4 md:w-96">
        <div className="bg-white rounded-2xl shadow-2xl border-2 border-emerald-500 p-6 animate-slide-up">
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
            aria-label="Dismiss"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex items-start gap-4 mb-4">
            <div className="bg-emerald-100 p-3 rounded-xl">
              <Smartphone className="w-6 h-6 text-emerald-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-gray-900 mb-1">
                Install Sentlo App
              </h3>
              <p className="text-sm text-gray-600">
                Add Sentlo to your home screen for quick access
              </p>
            </div>
          </div>

          <div className="space-y-3 mb-4">
            <div className="flex gap-3">
              <div className="bg-emerald-50 rounded-lg px-2 py-1 text-emerald-600 font-bold text-sm">1</div>
              <p className="text-sm text-gray-700 flex-1">
                Tap the <strong>Share</strong> button <span className="inline-block bg-blue-500 text-white px-2 py-0.5 rounded text-xs">⬆️</span> at the bottom of your screen
              </p>
            </div>
            <div className="flex gap-3">
              <div className="bg-emerald-50 rounded-lg px-2 py-1 text-emerald-600 font-bold text-sm">2</div>
              <p className="text-sm text-gray-700 flex-1">
                Scroll down and tap <strong>"Add to Home Screen"</strong>
              </p>
            </div>
            <div className="flex gap-3">
              <div className="bg-emerald-50 rounded-lg px-2 py-1 text-emerald-600 font-bold text-sm">3</div>
              <p className="text-sm text-gray-700 flex-1">
                Tap <strong>"Add"</strong> to complete installation
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 text-xs text-emerald-600 bg-emerald-50 p-3 rounded-lg">
            <Check className="w-4 h-4" />
            <span>Works offline • Faster loading • App-like experience</span>
          </div>
        </div>
      </div>
    );
  }

  // Android/Desktop - Native Install Prompt
  return (
    <div className="fixed bottom-20 left-4 right-4 z-50 md:left-auto md:right-4 md:w-96">
      <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl shadow-2xl p-6 text-white animate-slide-up">
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 text-white/80 hover:text-white"
          aria-label="Dismiss"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-start gap-4 mb-4">
          <div className="bg-white/20 p-3 rounded-xl backdrop-blur-sm">
            {platform === 'desktop' ? (
              <Monitor className="w-6 h-6 text-white" />
            ) : (
              <Smartphone className="w-6 h-6 text-white" />
            )}
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold mb-1">
              Install Sentlo {platform === 'desktop' ? 'Desktop App' : 'App'}
            </h3>
            <p className="text-sm text-white/90">
              Get instant access to traffic, power, fuel & market updates
            </p>
          </div>
        </div>

        <div className="space-y-2 mb-4 text-sm">
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4" />
            <span>⚡ Works offline - no internet needed</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4" />
            <span>🚀 Faster than browser version</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4" />
            <span>📱 Quick access from home screen</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4" />
            <span>🔔 Push notifications for alerts</span>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleInstallClick}
            disabled={isInstalling}
            className="flex-1 bg-white text-emerald-600 font-bold py-3 px-4 rounded-xl hover:bg-emerald-50 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isInstalling ? (
              <>
                <div className="w-5 h-5 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                Installing...
              </>
            ) : (
              <>
                <Download className="w-5 h-5" />
                Install Now
              </>
            )}
          </button>
          <button
            onClick={handleDismiss}
            className="px-4 py-3 text-white/90 hover:text-white font-medium"
          >
            Later
          </button>
        </div>

        <p className="text-xs text-white/70 text-center mt-3">
          Free • No app store needed • Takes only 5 seconds
        </p>
      </div>
    </div>
  );
}

// Add animation styles
const styles = `
  @keyframes slide-up {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  .animate-slide-up {
    animation: slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}
